import React, { useState, useEffect } from "react";
import {
	Table,
	Button,
	Modal,
	Form,
	Input,
	Row,
	Col,
	Space,
	Card,
	notification,
	Badge,
	Tag,
} from "antd";
import { UsergroupAddOutlined, EditOutlined } from "@ant-design/icons";
import teamGroupService from "../../services/teamGroup";
import DefaultLayout from "../../components/layout/DefaultLayout";
import Header from "../../components/header";
import { useMediaQuery } from "react-responsive";
import FormListComponent from "./../../components/formList/index";
import UserComponent from "./../../components/user/index";
import LocationComponent from "./../../components/location/index";

const TeamGroup = () => {
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [edit, setEdit] = useState(null);
	const [form] = Form.useForm();
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [totalDataCount, setTotalDataCount] = useState(0);
	const [itemsPerPage, setItemsPerPage] = useState(15);
	const [managerData, setManagerData] = useState([
		{ forms: undefined, locations: undefined, managers: undefined },
	]);

	// --- THIS FUNCTION IS NOW CORRECTED FOR YOUR API RESPONSE ---
	const getManagers = async (page = 1) => {
		setLoading(true);
		try {
			const response = await teamGroupService.getManagersUP(page, itemsPerPage);

			// The array of teams is in response.data (Correct)
			setData(response.data);

			// FIX: Pagination details are inside response.meta
			setTotalDataCount(response.meta.total);
			setItemsPerPage(response.meta.per_page);
			setCurrentPage(response.meta.current_page);
		} catch (error) {
			notification.error({
				message: "Error",
				description: "Failed to fetch team groups.",
			});
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getManagers(1);
	}, []);

	// No other changes are needed in the rest of the file.
	// The logic for modals, forms, and columns remains the same.
	const showModal = () => setIsModalVisible(true);

	const handleCancel = () => {
		setIsModalVisible(false);
		setEdit(null);
		form.resetFields();
	};

	const handleEdit = (record) => {
		setEdit(record);
		setIsModalVisible(true);
		const selectedManagerData = record.forms.map((form) => ({
			forms: form.id,
		}));
		setManagerData(selectedManagerData);
		form.setFieldsValue({
			name: record.name,
			form_ids: record.forms.map((form) => form.id),
			location_ids: record.locations.map((location) => location.id),
			manager_ids: record.managers.map((manager) => manager.id),
		});
	};

	const onFinish = async (values) => {
		setLoading(true);
		try {
			const serviceCall = edit
				? teamGroupService.updateManager(edit.id, values)
				: teamGroupService.postManager(values);

			const response = await serviceCall;

			if (response.success) {
				setIsModalVisible(false);
				setEdit(null);
				form.resetFields();
				notification.success({
					message: `Manager ${edit ? "Updated" : "Added"}`,
					description: response.message,
				});
				getManagers(currentPage);
			}
		} catch (error) {
			notification.error({
				message: "Error",
				description:
					error.response?.data?.message || "An unexpected error occurred.",
			});
		} finally {
			setLoading(false);
		}
	};

	const columns = [
		{
			title: "Group Name",
			dataIndex: "name",
			key: "name",
			fixed: useMediaQuery({ minWidth: 768 }) ? "left" : null,
			sorter: (a, b) => a.name.localeCompare(b.name),
		},
		{
			title: "Forms",
			dataIndex: "forms",
			key: "forms",
			render: (forms) => (
				<div>
					{forms.map((form) => (
						<Tag key={form.id}>{form.name}</Tag>
					))}
				</div>
			),
		},
		{
			title: "Manager",
			dataIndex: "managers",
			key: "managers",
			render: (managers) => (
				<div>
					{managers.map((manager) => (
						<Tag key={manager.id}>{manager.name}</Tag>
					))}
				</div>
			),
		},
		{
			title: "Locations",
			dataIndex: "locations",
			key: "locations",
			render: (locations) => (
				<div>
					{locations.map((location) => (
						<Tag key={location.id}>{location.name}</Tag>
					))}
				</div>
			),
		},
		{
			title: "Actions",
			key: "actions",
			fixed: "right",
			width: 80,
			render: (text, record) => (
				<Space>
					<div onClick={() => handleEdit(record)} style={{ cursor: "pointer" }}>
						<EditOutlined />
					</div>
				</Space>
			),
		},
	];

	const removeManagerGroup = (index) => {
		setManagerData((prevData) => prevData.filter((_, i) => i !== index));
	};
	const renderManagerDetails = () => {
		return managerData.map((data, index) => (
			<div key={`team-group-${index}`}>
				<Card
					size='small'
					title={
						<span>
							<Badge color='#2d77fa' count={index + 1} /> Manager Details
						</span>
					}
					className='mb-10'
				>
					<Row gutter={[12, 12]}>
						<Col span={8}>
							<Form.Item
								name={["form_ids", index]}
								label={`Form`}
								rules={[{ required: true, message: "Please select Form" }]}
							>
								<FormListComponent workflowOnly={false} />
							</Form.Item>
						</Col>

						<Col span={8}>
							<Form.Item
								name={["manager_ids", index]}
								label={`Manager`}
								rules={[{ required: true, message: "Please select Manager" }]}
							>
								<UserComponent />
							</Form.Item>
						</Col>

						<Col span={8}>
							<Form.Item
								name={["location_ids", index]}
								label={`Location`}
								rules={[{ required: true, message: "Please select Location" }]}
							>
								<LocationComponent all />
							</Form.Item>
						</Col>
					</Row>
				</Card>
				{index > 0 && (
					<div className='textRight mb-10'>
						<Button type='dashed' onClick={() => removeManagerGroup(index)}>
							Remove
						</Button>
					</div>
				)}
			</div>
		));
	};
	const handleAddManager = () => {
		setManagerData((prevData) => [
			...prevData,
			{ forms: undefined, locations: undefined, managers: undefined },
		]);
	};
	const handleAddManagerForNewRecord = () => {
		setManagerData((prevData) => [
			{ forms: undefined, locations: undefined, managers: undefined },
		]);
	};

	return (
		<DefaultLayout>
			<Row gutter={[24, 24]}>
				<Col span={24}>
					<Header
						icon={<UsergroupAddOutlined />}
						title='Create Team'
						right={
							<Button
								className='btn-blue'
								type='primary'
								onClick={() => {
									showModal();
									handleAddManagerForNewRecord();
								}}
							>
								Add Team Group
							</Button>
						}
					/>
				</Col>
			</Row>
			<Card>
				<Table
					scroll={{ x: 1000 }}
					style={{ minHeight: "100vh" }}
					loading={loading}
					columns={columns}
					dataSource={data.map((item) => ({ ...item, key: item.id }))}
					rowKey={(record) => record.id}
					pagination={{
						total: totalDataCount,
						current: currentPage,
						pageSize: itemsPerPage,
						showSizeChanger: false,
						onChange: (page) => getManagers(page),
					}}
				/>
			</Card>
			<Modal
				style={{ top: 14 }}
				title={edit ? "Edit Team Group" : "Add Team Group"}
				open={isModalVisible}
				onCancel={handleCancel}
				footer={null}
				width={900}
			>
				<Form
					form={form}
					onFinish={onFinish}
					layout='vertical'
					className='mt-20'
				>
					<Form.Item
						name='name'
						label='Group Name'
						rules={[{ required: true, message: "Please enter the group name" }]}
					>
						<Input size='large' />
					</Form.Item>
					{renderManagerDetails()}
					<div
						style={{ display: "flex", justifyContent: "space-between" }}
						className='mb-20'
					>
						<Button type='dashed' onClick={handleAddManager}>
							Add Team Manager
						</Button>
					</div>
					<Form.Item>
						<Button type='primary' htmlType='submit' loading={loading}>
							{edit ? "Save Changes" : "Add Team Group"}
						</Button>
					</Form.Item>
				</Form>
			</Modal>
		</DefaultLayout>
	);
};

export default TeamGroup;
